import { useContext, useMemo } from 'react';
import { InstrumentationContext } from '@bingads-webui-react/hoc-with-instrumentation';
import { createFlightInstrumentation } from './create-flight-instrumentation';
import type { FlightActivities } from './create-flight-instrumentation';

export type { FlightActivities };

/**
 * Provides the three activities every flight hook needs:
 *  - flightActivity:    logged when the user is in the experiment flight
 *  - nonFlightActivity: logged when the user is not in any flight
 *  - allFlightActivity: traces parse/fetch lifecycle for all flight data
 *
 * Falls back to a no-op scenario when InstrumentationContext is unavailable,
 * so the hooks can be used in contexts without instrumentation. Delegates to the
 * non-hook `createFlightInstrumentation` so the synchronous flight resolver emits
 * identical telemetry.
 *
 * @param {string} scenarioName - Scenario name; the three activity names derive from it.
 * @returns {FlightActivities} The flight / non-flight / all activities.
 */
export const useFlightInstrumentation = (scenarioName: string): FlightActivities => {
  const instrumentation = useContext(InstrumentationContext);

  return useMemo(
    () => createFlightInstrumentation(instrumentation, scenarioName),
    [instrumentation, scenarioName]
  );
};
